import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Paper,
} from "@mui/material";
import { SketchPicker } from "react-color";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import api from "../../utils/api";

const PriorityLevelsPage = () => {
  const [priorityLevels, setPriorityLevels] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    priority_value: "",
    display_name: "",
    color_code: "#000000",
  });
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    fetchPriorities();
  }, []);

  const fetchPriorities = async () => {
    try {
      const res = await api.get("/priority-levels");
      setPriorityLevels(res.data);
    } catch (err) {
      console.error("Failed to load priorities:", err);
    }
  };

  const handleOpen = (editData = null) => {
    setIsEditMode(!!editData);
    setForm(
      editData || {
        priority_value: "",
        display_name: "",
        color_code: "#000000",
      }
    );
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleSave = async () => {
    try {
      if (isEditMode) {
        await api.put(`/priority-levels/${form.priority_value}`, form);
      } else {
        await api.post("/priority-levels", form);
      }
      fetchPriorities();
      handleClose();
    } catch (err) {
      console.error("Error saving priority:", err);
    }
  };

  const handleDelete = async (priority_value) => {
    if (window.confirm("Are you sure you want to delete this priority?")) {
      try {
        await api.delete(`/priority-levels/${priority_value}`);
        fetchPriorities();
      } catch (err) {
        console.error("Error deleting priority:", err);
      }
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#fff",
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 1100,
        mx: "auto",
        mt: 1,
        p: { xs: 2, md: 3 },
        borderRadius: "18px",
        border: "1px solid rgba(47,99,173,0.08)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        backgroundColor: "rgba(255,255,255,0.96)",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: "#223142",
          mb: 0.5,
        }}
      >
        Priority Levels
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "#6b778c",
          mb: 3,
        }}
      >
        Manage priority values, display names, and color codes used across the system.
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpen()}
        sx={{
          backgroundColor: "#2f63ad",
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 700,
          boxShadow: "none",
          mb: 3,
          "&:hover": {
            backgroundColor: "#1f4f94",
            boxShadow: "none",
          },
        }}
      >
        Add Priority
      </Button>

      <Grid container spacing={2}>
        {priorityLevels.map((priority) => (
          <Grid item xs={12} sm={6} md={4} key={priority.priority_value}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: "16px",
                border: "1px solid rgba(47,99,173,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 28px rgba(0,0,0,0.10)",
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#223142",
                      mb: 0.5,
                    }}
                  >
                    {priority.display_name}
                  </Typography>

                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Value: {priority.priority_value}
                  </Typography>

                  <Chip
                    label={priority.color_code}
                    size="small"
                    sx={{
                      backgroundColor: priority.color_code,
                      color: "#fff",
                      mt: 1.25,
                      fontWeight: 600,
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() => handleOpen(priority)}
                      sx={{
                        border: "1px solid #e8eef5",
                        borderRadius: "10px",
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDelete(priority.priority_value)}
                      sx={{
                        border: "1px solid #e8eef5",
                        borderRadius: "10px",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {!priorityLevels.length && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "14px",
                backgroundColor: "#fbfcfe",
                border: "1px solid #eef2f7",
              }}
            >
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                No priority levels found.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={openDialog}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEditMode ? "Edit Priority" : "Add Priority"}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Priority Value"
            value={form.priority_value}
            disabled={isEditMode}
            onChange={(e) =>
              setForm({ ...form, priority_value: e.target.value })
            }
            sx={{ mb: 2, mt: 1, ...fieldSx }}
          />

          <TextField
            fullWidth
            label="Display Name"
            value={form.display_name}
            onChange={(e) =>
              setForm({ ...form, display_name: e.target.value })
            }
            sx={{ mb: 2, ...fieldSx }}
          />

          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: "#223142",
            }}
          >
            Select Color
          </Typography>

          <Box
            sx={{
              display: "inline-block",
              p: 1.5,
              borderRadius: "14px",
              backgroundColor: "#fbfcfe",
              border: "1px solid #eef2f7",
            }}
          >
            <SketchPicker
              color={form.color_code}
              onChangeComplete={(color) =>
                setForm({ ...form, color_code: color.hex })
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleClose}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: "#2f63ad",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1f4f94",
                boxShadow: "none",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PriorityLevelsPage;

